/**
 * Wire-mapping for the brokered-sessions family (OIDCPAM Gate-6 Phase 5c —
 * BrokeredSessionsPage.tsx / BrokeredSessionDetailPage.tsx / CommandLogView.tsx).
 *
 * AdminListBrokeredSessionsResp.sessions, AdminBrokeredSessionResp (monitor/
 * pause/resume/kill), and AdminGetBrokeredSessionRecordingsResp.entries are
 * typed `Array<Record<string, unknown>>` / `{}` by the generator (an opaque,
 * polymorphic response schema — see gen/client/react/client.ts), but the
 * server actually writes the snake_case BrokeredSession / SessionCommandLogEntry
 * JSON (see ./types.ts: subject_id, resource_id, driver_type, idle_expires_at,
 * abs_expires_at, created_at, terminated_at, termination_reason, seq_num,
 * entry_type, recorded_at, actor_sub, ...) and the generated client's
 * fromWire() converts every key to camelCase before resolving (client.ts's
 * schema-aware camelCase boundary mapper). So at this boundary each item
 * really is WireBrokeredSession / WireSessionCommandLogEntry below, NOT the
 * hand-written snake_case types in ./types.
 *
 * LATENT BUG FIXED BY THIS MAPPING: the pre-migration BrokeredSessionsPage.tsx
 * / BrokeredSessionDetailPage.tsx / CommandLogView.tsx cast these opaque
 * items straight to the hand-written SNAKE_CASE BrokeredSession /
 * SessionCommandLogEntry types in ./types (via `as unknown as`), which does
 * not match the real camelCase runtime shape. Every subject_id/resource_id/
 * driver_type/idle_expires_at/abs_expires_at/created_at/terminated_at/
 * termination_reason read off a session, and every seq_num/entry_type/
 * recorded_at read off a command-log entry, rendered `undefined` in
 * production (only `id`/`state` on sessions, and `command`/`filtered`/
 * `approved` on log entries, are single-word keys that happen to survive the
 * cast unchanged — everything else silently went blank). This mapping
 * (camelCase runtime shape -> @devnw/cyber-iam/iam/types camelCase props)
 * fixes all of these as a side effect, and pre-formats the date fields (the
 * library never parses display strings — see BrokeredSession/
 * SessionCommandLogEntry's doc comments in @devnw/cyber-iam/iam/types) via
 * `toLocaleString()`, matching the pre-migration pages' own
 * `new Date(...).toLocaleString()` calls exactly.
 */
import type {
  BrokeredSession as IamBrokeredSession,
  BrokeredSessionState,
  SessionCommandLogEntry as IamSessionCommandLogEntry,
} from '@devnw/cyber-iam/iam/types'

/**
 * The real (camelCase) runtime shape of one AdminListBrokeredSessionsResp
 * item, or the whole body of AdminBrokeredSessionResp (initiate/get/monitor/
 * pause/resume/kill all resolve the full session object directly, not
 * wrapped in a `.session` field).
 */
export interface WireBrokeredSession {
  id: string
  tenantId: string
  subjectId: string
  resourceId: string
  grantId?: string
  driverType: string
  state: BrokeredSessionState
  idleExpiresAt: string
  absExpiresAt: string
  createdAt: string
  terminatedAt?: string
  terminationReason?: string
}

/** The real (camelCase) runtime shape of one
 *  AdminGetBrokeredSessionRecordingsResp entry. */
export interface WireSessionCommandLogEntry {
  id: string
  tenantId: string
  sessionId: string
  seqNum: number
  entryType: string
  command?: string
  filtered: boolean
  approved: boolean
  recordedAt: string
  actorSub: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamBrokeredSession(w: WireBrokeredSession): IamBrokeredSession {
  return {
    id: w.id,
    subjectId: w.subjectId,
    resourceId: w.resourceId,
    driverType: w.driverType,
    state: w.state,
    grantId: w.grantId,
    createdAt: fmtDate(w.createdAt),
    idleExpiresAt: fmtDate(w.idleExpiresAt),
    absExpiresAt: fmtDate(w.absExpiresAt),
    terminatedAt: fmtDate(w.terminatedAt),
    terminationReason: w.terminationReason,
  }
}

export function toIamSessionCommandLogEntry(
  w: WireSessionCommandLogEntry
): IamSessionCommandLogEntry {
  return {
    id: w.id,
    seqNum: w.seqNum,
    entryType: w.entryType,
    filtered: w.filtered,
    approved: w.approved,
    command: w.command,
    recordedAt: fmtDate(w.recordedAt),
    actorSub: w.actorSub,
  }
}
